/**
 * FirstThenEditor Component
 * Specialized editor for First-Then boards with two panels: "First" (task) and "Then" (reward).
 */
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ScheduleStep } from '../../types/schedule';
import { Action } from '../../types/action';

interface FirstThenEditorProps {
  steps: ScheduleStep[];
  deviceActions: Action[];
  onAddStep: (position: 'first' | 'then') => void;
  onEditStep: (step: ScheduleStep) => void;
  onDeleteStep: (step: ScheduleStep) => void;
}

export const FirstThenEditor: React.FC<FirstThenEditorProps> = ({
  steps,
  deviceActions,
  onAddStep,
  onEditStep,
  onDeleteStep,
}) => {
  const { t } = useTranslation();

  // First-Then boards have exactly 2 steps: First (index 0) and Then (index 1)
  const firstStep = steps.find(s => s.order === 0);
  const thenStep = steps.find(s => s.order === 1);

  const getActionForStep = (step: ScheduleStep | undefined) => {
    if (!step?.actionId) return null;
    return deviceActions.find(a => a.id === step.actionId);
  };

  const firstAction = getActionForStep(firstStep);
  const thenAction = getActionForStep(thenStep);

  const renderPanel = (
    label: string,
    step: ScheduleStep | undefined,
    action: Action | null | undefined,
    position: 'first' | 'then',
    color: string
  ) => (
    <Paper
      elevation={2}
      sx={{
        flex: 1,
        minHeight: 280,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: 2,
        borderColor: color,
      }}
    >
      {/* Panel header */}
      <Box
        sx={{
          bgcolor: color,
          color: 'white',
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {label}
        </Typography>
        {step && (
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteStep(step);
              }}
              sx={{ color: 'white' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Panel content */}
      <Box sx={{ flex: 1, p: 2 }}>
        {step ? (
          <Card
            sx={{
              height: '100%',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 4,
              },
            }}
            onClick={() => onEditStep(step)}
          >
            <CardContent
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              {/* Action image */}
              {action?.thumbnailUrl || action?.pictureUrl ? (
                <Box
                  component="img"
                  src={action.thumbnailUrl ?? action.pictureUrl ?? undefined}
                  alt={action.name}
                  sx={{
                    width: 100,
                    height: 100,
                    objectFit: 'cover',
                    borderRadius: 2,
                    mb: 2,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: alpha(color, 0.1),
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    fontSize: '3rem',
                  }}
                >
                  {position === 'first' ? '1️⃣' : '🎁'}
                </Box>
              )}

              {/* Step title */}
              <Typography variant="h6" fontWeight={500}>
                {step.title}
              </Typography>

              {/* Subtitle if present */}
              {step.subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {step.subtitle}
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {t('schedules.steps.clickToChange')}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Card
            sx={{
              height: '100%',
              border: '2px dashed',
              borderColor: 'grey.300',
              bgcolor: 'transparent',
              cursor: 'pointer',
              '&:hover': {
                borderColor: color,
                bgcolor: alpha(color, 0.05),
              },
            }}
          >
            <CardActionArea
              onClick={() => onAddStep(position)}
              sx={{ height: '100%' }}
            >
              <CardContent
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AddIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography color="text.secondary">
                  {t('schedules.steps.addStep')}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        )}
      </Box>
    </Paper>
  );

  return (
    <Box>
      {/* Instructions */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('schedules.firstThen.instructions')}
      </Typography>

      {/* Two-panel layout */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {/* FIRST panel */}
        {renderPanel(
          t('schedules.firstThen.first'),
          firstStep,
          firstAction,
          'first',
          '#2196f3' // Blue
        )}

        {/* Arrow between panels */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
          }}
        >
          <ArrowIcon sx={{ fontSize: 48, color: 'grey.400' }} />
        </Box>

        {/* THEN panel */}
        {renderPanel(
          t('schedules.firstThen.then'),
          thenStep,
          thenAction,
          'then',
          '#4caf50' // Green
        )}
      </Box>
    </Box>
  );
};
